import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

const Container = styled.div({
  position: "relative",
});

const InputBox = styled.div<{ selectedValue: string }>(
  ({ theme, selectedValue }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontWeight: theme.fontWeight.regular,
    fontSize: "1.6rem",
    lineHeight: "1.6rem",
    padding: "1.6rem",
    border: `1px solid ${theme.color.black}`,
    zIndex: 10,
    color:
      selectedValue === "선택" ? theme.color.light_gray : theme.color.black,
  }),
);

const SelectBox = styled(motion.div)<{ open: boolean }>(({ theme, open }) => ({
  display: open ? "flex" : "none",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "absolute",
  top: "5.3rem",
  left: "0",
  width: "100%",
  backgroundColor: theme.color.white,
  zIndex: 1001,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
}));

const OptionItem = styled.button<{ last: boolean }>(({ theme, last }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  padding: "1.6rem",
  fontSize: "1.6rem",
  lineHeight: "2.4rem",
  fontWeight: theme.fontWeight.medium,
  color: theme.color.black,
  backgroundColor: theme.color.white,
  border: "none",
  textAlign: "start",
  borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
  ":hover": {
    backgroundColor: theme.color.border_gray,
  },
}));

const Left = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
}));

const Input = styled.input(() => ({
  fontSize: "1.6rem",
}));

type Props = {
  options: any[];
  name: string;
  setSelectedKey: React.Dispatch<number>;
};
const BrandDropdownInput = ({ options = [], name, setSelectedKey }: Props) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("선택");
  const [inputValue, setInputValue] = useState("");
  const { setValue, watch } = useFormContext();
  const { t } = useTranslation();
  const lang = window.localStorage.getItem("language");

  const handleToggle = () => {
    if (options.length === 0) {
      return;
    }
    setOpen(!open);
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setValue("brand_name", e.target.value);
  };

  const handleChange = (value: string, brandCode: number) => {
    setOpen(false);
    setSelectedValue(value);
    setInputValue(value);
    setValue("brand_name", value);
    setValue(name, brandCode);
    setSelectedKey(brandCode);
  };

  const filteredOptions = options?.filter((option) =>
    option[`name_${lang}`].toLowerCase().includes(inputValue.toLowerCase()),
  );

  useEffect(() => {
    if (watch(name) === null) {
      // setSelectedValue("선택");
      setInputValue("");
    } else {
      const selectedOption = options.find(
        (option) => option.code === watch(name),
      );
      if (selectedOption) {
        setSelectedValue(selectedOption[`name_${lang}`]);
        setInputValue(selectedOption[`name_${lang}`]);
        setValue("brand_name", selectedOption[`name_${lang}`]);
      }
    }
  }, [watch(name), options]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2 }}>
        <Container>
          <InputBox onClick={handleToggle} selectedValue={selectedValue}>
            <Left>
              <Image src={theme.image.search_icon.default} size="1.8rem" />
              <Input
                value={inputValue}
                onChange={handleChangeInput}
                placeholder={t("검색")}
              />
            </Left>

            {!open && (
              <Image src={theme.image.arrow_right.default} size="2rem" />
            )}
          </InputBox>

          <SelectBox
            open={open}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: open ? 1 : 0, y: open ? 0 : -10 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1 }}>
            {filteredOptions?.map((item, index) => (
              <OptionItem
                type="button"
                key={index}
                last={index === options.length - 1}
                onClick={() => handleChange(item[`name_${lang}`], item.code)}>
                {item[`name_${lang}`]}

                {item[`name_${lang}`] === selectedValue && (
                  <Image src={theme.image.check_green.default} />
                )}
              </OptionItem>
            ))}
          </SelectBox>
        </Container>
      </motion.div>
    </AnimatePresence>
  );
};

export default BrandDropdownInput;
